import { Component } from 'react';
import styles from './ErrorBoundary.module.css';

/**
 * Class-based error boundary. React 18 has no hook equivalent for
 * componentDidCatch, so this must remain a class component.
 * Catches render-time exceptions in its subtree and shows a minimal
 * fallback UI instead of letting the whole app crash to a white screen.
 *
 * Reusable across nesting levels: the app wraps the whole tree with one
 * instance as a last-resort net (see index.js), and individual widgets
 * (e.g. a chart card) can wrap themselves with a second, more narrowly
 * scoped instance so a fault in that widget doesn't blank everything
 * else on the page. `message` lets a caller customize the fallback text
 * for its scope; it defaults to the original generic copy so the
 * existing global usage in index.js is unaffected.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const { message = 'Something went wrong.' } = this.props;
      return (
        <div className={styles.fallback} role="alert">
          <p>{message}</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
